import type { UseFormSetError } from 'react-hook-form';
import type { User } from '@/utils/types/User';
import { type Renter } from '../../utils/types/renter.interface';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box } from '@mui/material';

import { Divider } from '@/components/Divider';
import { checkIfPmi } from '@/utils/commonUtils';
import {
  MONTHLY_RENT_SDR_LIMIT_BY_USER_TYPE,
  SELF_ENROLLED_BUILDING_ATTR,
} from '@/utils/constants';
import { RentCoverageTypes, SecurityDepositTypes } from '../../constants/CoverageType';
import { CoverageRequirementsAlert } from './content/RenterInviteDrawerForm.CoverageRequirementsAlert';
import CoverageSelection from './content/RenterInviteDrawerForm.CoverageSelection';
import { LeaseInformation } from './content/RenterInviteDrawerForm.LeaseInformation';
import { RenterContact } from './content/RenterInviteDrawerForm.RenterContact';
import ScreeningResults from './content/RenterInviteDrawerForm.ScreeningResults';
import useRenterInviteForm from './useRenterInviteForm';
import { parseMoney } from '@/utils/ParseMoney';
import { Button, theme } from '@theguarantors/tg-package-material-ui';
import useRenterInviteDrawerStore from '@/stores/useRenterInviteDrawerStore';
import {
  calculateGrossTotalCoverage,
  calculateLeaseDurationMonths,
  calculateMonthsOfCoverage,
  coverageMonthCountOptions,
  isCoverageOptionEnabled,
} from './utils/coverage.util';
import { Tooltip } from '@/components/Tooltip';
import { useInviteTracking } from './hooks';
import { InviteSegmentEvents } from './constants';
import type { InviteFlowMilestoneKey, StartAction } from './RenterInvite.types';

interface RenterInviteDrawerFormProps {
  onSubmit: (
    values: Renter,
    setError: UseFormSetError<Renter>,
    submitAttemptNumber?: number,
  ) => void;
  errorMsg: string;
  user?: User;
  newCoveragesEnabled?: boolean;
  customCoverageDisabled?: boolean;
  isSmbRenterInvites?: boolean;
  onMilestoneReached?: (key: InviteFlowMilestoneKey) => void;
}

const RenterInviteDrawerFormDivider = () => (
  <Divider
    sx={{
      borderColor: `${theme.palette.neutralGrey[200]} !important`,
      my: 3,
      [theme.breakpoints.down('md')]: {
        borderColor: `transparent !important`,
        // height: '24px !important',
        color: 'transparent !important',
        my: 1.5,
      },
    }}
  />
);

const RenterInviteDrawerForm = ({
  onSubmit,
  errorMsg,
  user,
  newCoveragesEnabled,
  customCoverageDisabled = false,
  isSmbRenterInvites = true,
  onMilestoneReached,
}: RenterInviteDrawerFormProps) => {
  const [isSelfEnrolled, setIsSelfEnrolled] = useState<boolean>(false);
  const { trackInvite } = useInviteTracking();
  const flowStartedRef = useRef(false);
  const submitAttemptRef = useRef(0);

  const selfEnrolledSDRLimit = useMemo(() => {
    if (!user) return;
    if (checkIfPmi(user)) {
      return MONTHLY_RENT_SDR_LIMIT_BY_USER_TYPE.PMI;
    }
    if (isSelfEnrolled) {
      return MONTHLY_RENT_SDR_LIMIT_BY_USER_TYPE.SELF_ENROLLED;
    }
  }, [user, isSelfEnrolled]);

  const { handleSubmit, watch, control, trigger, setError, resetField, setValue, formState } =
    useRenterInviteForm({
      selfEnrolledSDRLimit,
      customCoverageDisabled,
      useNewCoveragesOnLandlordInvite: newCoveragesEnabled === true,
    });

  const { isSubmitting, isValid, errors, isDirty, dirtyFields } = formState;

  // Update store's dirty state ref during render (no useEffect needed)
  // Accessing isDirty and dirtyFields here subscribes to react-hook-form updates
  const { dirtyStateRef } = useRenterInviteDrawerStore();
  dirtyStateRef.current = { isDirty, dirtyFields };

  const customCoverage = watch('customCoverage');
  const address = watch('address');

  useEffect(() => {
    // Skip when customCoverage is undefined (happens during re-renders with shouldUnregister: true)
    if (customCoverage === undefined) {
      return;
    }

    if (customCoverage) {
      resetField('approvalStatus');
      setValue('rentCoverage', RentCoverageTypes.Customize);
      setValue('securityDepositCoverage', SecurityDepositTypes.CustomizeOrRemove);
    } else {
      setValue('rentCoverage', RentCoverageTypes.CompanyDefault);
      setValue('securityDepositCoverage', SecurityDepositTypes.CompanyDefault);
    }
  }, [customCoverage, resetField, setValue]);

  useEffect(() => {
    setIsSelfEnrolled(address?.source === SELF_ENROLLED_BUILDING_ATTR);
  }, [address]);

  const leaseStart = watch('leaseStart');
  const leaseEnd = watch('leaseEnd');
  const leaseDurationMonths = calculateLeaseDurationMonths(leaseStart, leaseEnd);
  const monthsOfCoverage = watch('monthsOfCoverage');

  // Reset monthsOfCoverage if the selected option becomes disabled due to lease duration change
  useEffect(() => {
    if (newCoveragesEnabled && monthsOfCoverage && !customCoverage) {
      const coverageValue =
        typeof monthsOfCoverage === 'string' ? Number(monthsOfCoverage) : monthsOfCoverage;
      const isCurrentSelectionEnabled = isCoverageOptionEnabled(coverageValue, leaseDurationMonths);
      if (!isCurrentSelectionEnabled) {
        setValue('monthsOfCoverage', undefined);
      }
    }
  }, [customCoverage, leaseDurationMonths, monthsOfCoverage, newCoveragesEnabled, setValue]);

  const grossMonthlyRent = watch('grossMonthlyRent');
  const freeRent = watch('freeRent');
  const prepaidRent = watch('prepaidRent');

  const grossTotalCoverage = calculateGrossTotalCoverage(
    monthsOfCoverage,
    grossMonthlyRent,
    freeRent,
    prepaidRent,
  );
  const netMonthsOfCoverage = calculateMonthsOfCoverage(grossMonthlyRent, grossTotalCoverage);

  const trackFlowStarted = useCallback(
    (startAction: StartAction) => {
      // Fires at most once per form mount; flowStartedRef resets on form key change.
      if (flowStartedRef.current) return;
      flowStartedRef.current = true;
      onMilestoneReached?.('hadFlowStarted');
      trackInvite(InviteSegmentEvents.FlowStarted, { start_action: startAction });
    },
    [trackInvite, onMilestoneReached],
  );

  const trackValidationErrors = useCallback(
    (fieldErrors: Record<string, unknown>, source: 'client' | 'server') => {
      const errors = Object.entries(fieldErrors)
        .filter(([, error]) => Boolean(error))
        .map(([fieldName, error]) => {
          const errorObj = error as { message?: string; type?: string };
          return {
            field_name: fieldName,
            error_code: errorObj.type ?? 'validation',
            error_message_shown: errorObj.message ?? '',
          };
        });

      if (errors.length === 0) return;

      trackInvite(InviteSegmentEvents.ValidationErrorsShown, {
        validation_source: source,
        error_category: 'form_validation',
        errors,
      });
    },
    [trackInvite],
  );

  const handleFormSubmit = handleSubmit(
    (values) => {
      submitAttemptRef.current += 1;
      onMilestoneReached?.('hadSubmitAttempt');

      const payload = newCoveragesEnabled
        ? { ...values, monthsOfCoverage: netMonthsOfCoverage }
        : values;

      return onSubmit(payload, setError, submitAttemptRef.current);
    },
    (fieldErrors) => {
      submitAttemptRef.current += 1;
      onMilestoneReached?.('hadSubmitAttempt');
      trackValidationErrors(fieldErrors, 'client');
    },
  );

  return (
    <form
      id={'renter-invite-form'}
      style={{ margin: 0 }}
      onSubmit={handleFormSubmit}
      onFocusCapture={() => trackFlowStarted('field_focus')}
      onClickCapture={() => trackFlowStarted('click')}
      onKeyDownCapture={() => trackFlowStarted('typing')}
      noValidate
    >
      {customCoverageDisabled && <CoverageRequirementsAlert />}
      <LeaseInformation
        control={control}
        errors={errors}
        watch={watch}
        setValue={setValue}
        trigger={trigger}
        onMilestoneReached={onMilestoneReached}
      />
      <RenterInviteDrawerFormDivider />
      <RenterContact control={control} errors={errors} watch={watch} trigger={trigger} />
      <RenterInviteDrawerFormDivider />
      <ScreeningResults
        control={control}
        errors={errors}
        watch={watch}
        setValue={setValue}
        isSmbInvites={isSmbRenterInvites}
        newCoveragesEnabled={newCoveragesEnabled}
        onMilestoneReached={onMilestoneReached}
        coverageLengthOptions={coverageMonthCountOptions.map((monthCount) => {
          const grossCoverage = calculateGrossTotalCoverage(
            monthCount,
            grossMonthlyRent,
            freeRent,
            prepaidRent,
          );

          const isEnabledByLeaseDuration = isCoverageOptionEnabled(monthCount, leaseDurationMonths);

          return {
            id: `coverage-${monthCount}-months`,
            value: monthCount.toString(),
            disabled: !isEnabledByLeaseDuration,
            text: `${monthCount.toString()} months`,
            subText: grossMonthlyRent
              ? `${parseMoney(grossCoverage) || '$0'} max coverage`
              : '$0 max coverage',
          };
        })}
      />
      {((isSmbRenterInvites && !!customCoverage) ||
        (!isSmbRenterInvites && !customCoverageDisabled)) && (
        <>
          <RenterInviteDrawerFormDivider />
          <CoverageSelection
            control={control}
            errors={errors}
            watch={watch}
            useDrawerStyles={true}
            selfEnrolledSDRLimit={selfEnrolledSDRLimit}
          />
        </>
      )}

      {errorMsg && (
        <Alert severity="error" sx={{ marginY: 2 }}>
          {errorMsg}
        </Alert>
      )}
      <Box
        sx={{
          position: 'sticky',
          bottom: -1,
          backgroundColor: 'background.paper',
          borderTop: `1px solid ${theme.palette.neutralGrey[200]}`,
          pt: 2,
          pb: 2,
          mt: 2,
          zIndex: 100,
          mx: -4,
          px: 4,
        }}
      >
        <Tooltip title={!isValid ? 'Please complete the required fields to proceed' : ''}>
          <Box>
            <Button
              variant="contained"
              type="submit"
              disabled={!isValid || isSubmitting}
              loading={isSubmitting}
              id={'RenterInviteSubmitButton'}
              style={!isValid || isSubmitting ? { pointerEvents: 'none' } : {}}
              sx={{ width: '100%', height: '55px' }}
            >
              Send Invite
            </Button>
          </Box>
        </Tooltip>
      </Box>
    </form>
  );
};

export default RenterInviteDrawerForm;
